/**
 * @CODE:SPEC-ACC-001-CARD-001
 * AccommodationCard Component
 *
 * Displays accommodation information in a card format.
 * Follows CourseCard design pattern with Framer Motion animations.
 *
 * Features:
 * - Responsive grid layout (mobile-first)
 * - Framer Motion animations (entrance, hover)
 * - Heart button for favorites (with loading state)
 * - Type badge with primary color
 * - React.memo optimization
 * - Accessibility support
 */

import { memo, useState, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Heart, Loader2, MapPin, Camera, Signpost } from "lucide-react";
import { Link } from "react-router-dom";
import type { AccommodationWithDetails } from "@/features/accommodation/types";
import { formatPrice, getMainImageUrl } from "@/features/accommodation/api";
import { getPlaceholderImage, hasValidImage } from "../utils/placeholderImages";
import { formatTextWithLineBreaks, removeEscapeSequences } from "../utils/textFormatters";
import { formatDistance } from "../utils/distanceFormatter";

interface AccommodationCardProps {
  accommodation: AccommodationWithDetails;
  index?: number;
  onFavoriteChange?: (
    accommodationId: number,
    isFavorite: boolean,
  ) => Promise<void>;
}

interface AccommodationCardState {
  isFavorite: boolean;
  isLoading: boolean;
}

const AccommodationCard = memo(
  ({ accommodation, index = 0, onFavoriteChange }: AccommodationCardProps) => {
    const shouldReduceMotion = useReducedMotion();
    const [state, setState] = useState<AccommodationCardState>({
      isFavorite: false,
      isLoading: false,
    });

    const imageUrl = useMemo(
      () => getMainImageUrl(accommodation),
      [accommodation],
    );

    const placeholderConfig = useMemo(
      () => getPlaceholderImage(accommodation.accommodation_type?.id),
      [accommodation.accommodation_type?.id],
    );

    const showPlaceholder = !hasValidImage(imageUrl);

    // Animation variants
    const cardVariants = {
      hidden: {
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          delay: shouldReduceMotion ? 0 : index * 0.1,
          ease: "easeOut" as const,
        },
      },
    };

    const heartVariants = {
      initial: { scale: 1 },
      tap: { scale: 0.85 },
      hover: {
        scale: state.isLoading ? 1 : 1.1,
        transition: { duration: 0.2 },
      },
      loading: {
        scale: 1,
        rotate: 360,
        transition: {
          rotate: {
            duration: 1,
            repeat: Infinity,
            ease: "linear",
          },
        },
      },
    };

    const handleFavoriteClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (state.isLoading || !onFavoriteChange) return;

      setState({ ...state, isLoading: true });

      try {
        const newFavoriteState = !state.isFavorite;
        await onFavoriteChange(
          accommodation.accommodation_id,
          newFavoriteState,
        );
        setState({
          isFavorite: newFavoriteState,
          isLoading: false,
        });
      } catch (error) {
        console.error("Failed to update favorite:", error);
        setState({ ...state, isLoading: false });
      }
    };

    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div
          className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group relative ${
            state.isLoading ? "opacity-90 pointer-events-none" : ""
          }`}
          whileHover={{
            y: shouldReduceMotion ? 0 : state.isLoading ? 0 : -5,
          }}
          transition={{ duration: 0.2 }}
          animate={{
            scale: state.isLoading ? 0.98 : 1,
            opacity: state.isLoading ? 0.9 : 1,
          }}
        >
          {/* Image Container */}
          <div className="relative h-48 overflow-hidden bg-gray-200">
            <Link to={`/stay/${accommodation.accommodation_id}`}>
              {showPlaceholder ? (
                <div className="relative w-full h-full">
                  {/* Blurred Background Image */}
                  <motion.img
                    src={placeholderConfig.imageUrl}
                    alt=""
                    className="w-full h-full object-cover filter blur-md brightness-75"
                    style={{ filter: 'blur(8px) brightness(0.75)' }}
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer">
                    <div className="text-center text-white px-4">
                      <Camera className="w-12 h-12 mx-auto mb-2 opacity-80" />
                      <p className="text-lg font-semibold mb-1">이미지 준비중</p>
                      <p className="text-sm opacity-90">자세한 정보는 문의해주세요</p>
                    </div>
                  </div>
                </div>
              ) : (
                <motion.img
                  src={imageUrl}
                  alt={accommodation.name}
                  className="w-full h-full object-cover cursor-pointer"
                  whileHover={{
                    scale: shouldReduceMotion ? 1 : 1.05,
                  }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </Link>

            {/* Type Badge */}
            {accommodation.accommodation_type && (
              <motion.div
                className="absolute top-3 left-3 bg-[#F39800] text-white px-2 py-1 rounded-full text-sm font-semibold pointer-events-none z-10"
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {accommodation.accommodation_type.name}
              </motion.div>
            )}

            {/* Heart Button */}
            <motion.button
              onClick={handleFavoriteClick}
              disabled={state.isLoading}
              className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 z-20 ${
                state.isLoading
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-white/80 hover:bg-white shadow-sm hover:shadow-md"
              }`}
              variants={heartVariants}
              initial="initial"
              animate={state.isLoading ? "loading" : "initial"}
              whileHover={!state.isLoading ? "hover" : "loading"}
              whileTap={!state.isLoading ? "tap" : "loading"}
              style={{ pointerEvents: "auto" }}
              title={state.isFavorite ? "찜 제거" : "찜하기"}
            >
              {state.isLoading ? (
                <Loader2 className="w-5 h-5 text-gray-500" />
              ) : (
                <Heart
                  className={`w-5 h-5 transition-colors ${
                    state.isFavorite
                      ? "fill-red-500 text-red-500"
                      : "text-gray-600 hover:text-red-400"
                  }`}
                />
              )}
            </motion.button>
          </div>

          {/* Content */}
          <Link to={`/stay/${accommodation.accommodation_id}`}>
            <div className="p-5 cursor-pointer">
              <h4 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1">
                {accommodation.name}
              </h4>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {formatTextWithLineBreaks(
                  removeEscapeSequences(accommodation.description)
                )}
              </p>

              <div className="flex items-center text-sm text-gray-500 mb-3">
                <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                <span className="truncate">
                  {accommodation.address || "위치 정보 없음"}
                </span>
              </div>

              {/* Trail Information (always show except for 제주올레길) */}
              {accommodation.trail_name && accommodation.trail_name !== "제주올레길" && (
                <motion.div
                  className="flex items-center text-sm text-gray-500 mb-3"
                  data-testid="trail-info"
                  initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <Signpost
                    className="w-4 h-4 mr-1 flex-shrink-0"
                    data-testid="trail-icon"
                  />
                  <span className="truncate">
                    {accommodation.trail_name}
                  </span>
                </motion.div>
              )}

              <div className="mb-4 text-base font-semibold text-gray-800">
                {formatPrice(accommodation.price_from)}
                <span className="text-sm text-gray-500 ml-1 font-normal">
                  / 박
                </span>
              </div>

              {accommodation.amenities &&
                accommodation.amenities.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-1">
                    {accommodation.amenities.slice(0, 2).map((amenity, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {amenity}
                      </span>
                    ))}
                    {accommodation.amenities.length > 2 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        +{accommodation.amenities.length - 2}
                      </span>
                    )}
                  </div>
                )}

              <div className="text-center pt-3 border-t">
                <span className="text-[#F39800] font-semibold hover:text-[#E08700] transition-colors">
                  자세히 보기 →
                </span>
              </div>
            </div>
          </Link>
        </motion.div>
      </motion.div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if accommodation data has actually changed
    return (
      prevProps.accommodation.accommodation_id ===
        nextProps.accommodation.accommodation_id &&
      prevProps.accommodation.name === nextProps.accommodation.name &&
      prevProps.accommodation.price_from ===
        nextProps.accommodation.price_from &&
      prevProps.accommodation.description ===
        nextProps.accommodation.description &&
      prevProps.accommodation.main_image.image_url ===
        nextProps.accommodation.main_image.image_url &&
      prevProps.accommodation.amenities?.join(",") ===
        nextProps.accommodation.amenities?.join(",") &&
      prevProps.accommodation.nearest_course?.course_id ===
        nextProps.accommodation.nearest_course?.course_id &&
      prevProps.accommodation.nearest_course?.distance_km ===
        nextProps.accommodation.nearest_course?.distance_km &&
      prevProps.index === nextProps.index
    );
  },
);

AccommodationCard.displayName = "AccommodationCard";

export default AccommodationCard;
